import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import type {PredictionConfig} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import {submitBulkPrediction} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import {
  getUseMSAForSubmission,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';

import {
  type ComponentKind,
  getComponentLabel,
  type PredictionComponent,
  type PredictionInput,
  normalizeValue,
} from './newPredictionState';

export interface BuildPredictionConfigsParams {
  inputs: Array<PredictionInput>;
  model: ScientificModelNode;
}

export interface RunNewPredictionSubmitParams extends BuildPredictionConfigsParams {
  batchName: string;
  msaEnabled: boolean;
}

interface RawComponentConfig {
  _type: ComponentKind;
  value: string;
  name: string;
}

/**
 * Expand one component into `copies` raw configs. The backend config schema has no stoichiometry
 * field, so N copies are materialized as N repeated entries. Each copy gets a numbered name suffix
 * (`Chain A-1`, `Chain A-2`, …); a single copy keeps the plain label (`Chain A`).
 */
function expandComponent(component: PredictionComponent, label: string): Array<RawComponentConfig> {
  const value = normalizeValue(component);
  const copies = Math.max(1, Math.floor(component.copies));
  if (copies === 1) {
    return [{_type: component.type, value, name: label}];
  }
  return Array.from(
    {length: copies},
    (_unused, copyIndex): RawComponentConfig => ({
      _type: component.type,
      value,
      name: `${label}-${copyIndex + 1}`,
    })
  );
}

/** Map a single input's components to one prediction config (one PredictionJob). */
function buildPredictionConfig(
  components: Array<PredictionComponent>,
  model: ScientificModelNode
): PredictionConfig {
  if (!model.capabilities.supportsMultimers) {
    // Monomer models submit a single component and ignore copies.
    const monomer = components[0];
    return {
      component: {
        _type: monomer.type,
        value: normalizeValue(monomer),
        name: getComponentLabel(components, 0),
      },
    };
  }
  const rawComponents = components.flatMap((component, index) =>
    expandComponent(component, getComponentLabel(components, index))
  );
  return {components: rawComponents};
}

export function buildPredictionConfigs({
  inputs,
  model,
}: BuildPredictionConfigsParams): Array<PredictionConfig> {
  return inputs.map((input) => buildPredictionConfig(input.components, model));
}

export async function runNewPredictionSubmit({
  inputs,
  batchName,
  model,
  msaEnabled,
}: RunNewPredictionSubmitParams): Promise<{predictionBatchId: string}> {
  return await submitBulkPrediction({
    model,
    predictionConfigs: buildPredictionConfigs({inputs, model}),
    jobNames: inputs.map((input) => input.name.trim()),
    modelParams: {useMsa: getUseMSAForSubmission(model, msaEnabled)},
    source: MODEL_RUN_SOURCE.MODEL_HUB_UI,
    ...(batchName.trim() ? {name: batchName.trim()} : {}),
  });
}
